/**
 * SO 配置庫存：物料顯示／需求量／mtrId 解析（契約：mtrs／inv_allocations）。
 */

import {
  getSalesOrderAllocationMtrId,
  getSalesOrderAllocationStockId,
  getSalesOrderLineInvAllocations,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderFieldAccessors';
import { getInvStockAvailableQty } from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';

export function formatSalesOrderMaterialLabel(material) {
  const mtrNo = material?.mtrNo ? String(material.mtrNo).trim() : '';
  const mtrName = material?.mtrName ? String(material.mtrName).trim() : '';
  const nestedNo = material?.material?.mtrNo ? String(material.material.mtrNo).trim() : '';
  const nestedName = material?.material?.name ? String(material.material.name).trim() : '';
  const no = mtrNo || nestedNo;
  const name = mtrName || nestedName;
  if (no && name) {
    return `${no} ${name}`;
  }
  return no || name || '—';
}

/** 優先 mtr.qty；缺則 qtyPerProduct × line.qty。 */
export function resolveSalesOrderMaterialRequiredQty(material, lineQty) {
  if (material?.qty != null && material.qty !== '') {
    const direct = Number(material.qty);
    if (Number.isFinite(direct)) {
      return direct;
    }
  }
  const qtyPerProduct = Number(material?.qtyPerProduct);
  const qty = Number(lineQty);
  if (!Number.isFinite(qtyPerProduct) || !Number.isFinite(qty)) {
    return null;
  }
  return qtyPerProduct * qty;
}

/** Detail mtrs：mtr_id／material_id／巢狀 material.id。 */
export function resolveSalesOrderMaterialMtrId(material) {
  if (material?.mtrId != null && material.mtrId !== '') {
    return String(material.mtrId);
  }
  if (material?.materialId != null && material.materialId !== '') {
    return String(material.materialId);
  }
  if (material?.material?.id != null && material.material.id !== '') {
    return String(material.material.id);
  }
  return null;
}

export function getAllocationsForMaterialSnapshot(line, mtrRowId) {
  if (!mtrRowId) {
    return [];
  }
  return getSalesOrderLineInvAllocations(line).filter(
    (allocation) => String(getSalesOrderAllocationMtrId(allocation)) === String(mtrRowId),
  );
}

export function sumAllocationQty(allocations) {
  return (allocations ?? []).reduce((sum, allocation) => {
    const qty = Number(allocation?.qty);
    return Number.isFinite(qty) ? sum + qty : sum;
  }, 0);
}

/**
 * 已配量顏色：已配 < 需求 → red；已配 ≥ 需求或需求無效／≤0 → primary。
 * （對齊 outbound primary 用法；不足用 text-red-500。）
 */
export function getSalesOrderAllocatedQtyValueClass(allocatedQty, requiredQty) {
  const allocated = Number(allocatedQty);
  const required = Number(requiredQty);
  if (!Number.isFinite(required) || required <= 0) {
    return 'text-primary-600';
  }
  if (Number.isFinite(allocated) && allocated < required) {
    return 'text-red-500';
  }
  return 'text-primary-600';
}

/** 整張 SO 內已被其他 material／line 佔用的 invStockId。 */
export function collectUsedInventoryStockIds(lines, {
  excludeLineIndex = null,
  excludeMtrRowId = null,
} = {}) {
  const used = new Set();
  (lines ?? []).forEach((line, lineIndex) => {
    getSalesOrderLineInvAllocations(line).forEach((allocation) => {
      const stockId = getSalesOrderAllocationStockId(allocation);
      if (stockId == null || stockId === '') {
        return;
      }
      const sameLine = excludeLineIndex != null && lineIndex === excludeLineIndex;
      const sameMtr = excludeMtrRowId != null
        && String(getSalesOrderAllocationMtrId(allocation)) === String(excludeMtrRowId);
      if (sameLine && sameMtr) {
        return;
      }
      used.add(String(stockId));
    });
  });
  return used;
}

export function findDuplicateInventoryStockId(lines) {
  const seen = new Set();
  for (const line of lines ?? []) {
    for (const allocation of getSalesOrderLineInvAllocations(line)) {
      const stockId = getSalesOrderAllocationStockId(allocation);
      if (stockId == null || stockId === '') {
        continue;
      }
      const key = String(stockId);
      if (seen.has(key)) {
        return key;
      }
      seen.add(key);
    }
  }
  return null;
}

export function mapInvStockRowToSalesOrderAllocation(row, mtrRowId, existingQty = '') {
  const availableQty = getInvStockAvailableQty(row);
  const hasExisting = existingQty !== '' && existingQty != null;
  const qty = hasExisting
    ? existingQty
    : (availableQty > 0 ? availableQty : '');

  return {
    salesOrderLineMtrId: mtrRowId,
    invStockId: row?.id != null ? String(row.id) : null,
    qty,
    availableQty,
    stockNo: row?.stockNo ?? null,
    warehouse: row?.warehouse
      ? {
        id: row.warehouse.id ?? null,
        code: row.warehouse.code ?? null,
        name: row.warehouse.name ?? null,
      }
      : null,
    warehouseName: row?.warehouse?.name ?? null,
  };
}

/**
 * inventory-candidates `candidates[]` → 表單 allocation。
 * stock identity＝`invStockId`（非 candidate.id）。
 */
export function mapInventoryCandidateToSalesOrderAllocation(
  candidate,
  mtrRowId,
  existingQty = '',
) {
  const availableQty = Number(candidate?.availableQty);
  const hasExisting = existingQty !== '' && existingQty != null;
  const qty = hasExisting
    ? existingQty
    : (
      Number.isFinite(availableQty) && availableQty > 0
        ? availableQty
        : (candidate?.selectedQty ?? '')
    );

  return {
    salesOrderLineMtrId: mtrRowId,
    invStockId: candidate?.invStockId != null ? String(candidate.invStockId) : null,
    qty,
    availableQty: Number.isFinite(availableQty) ? availableQty : null,
    stockNo: candidate?.stockNo ?? null,
    warehouse: candidate?.warehouse
      ? {
        id: candidate.warehouse.id ?? null,
        code: candidate.warehouse.code ?? null,
        name: candidate.warehouse.name ?? null,
      }
      : null,
    warehouseName: candidate?.warehouse?.name ?? null,
  };
}

/** 從 candidates response 取某 line mtr 的 candidates[]。 */
export function findSalesOrderInventoryCandidatesForMtr(candidatesData, {
  lineId,
  mtrRowId,
} = {}) {
  const lines = candidatesData?.lines ?? [];
  const line = lines.find((row) => String(row?.id) === String(lineId));
  const mtrs = line?.mtrs ?? [];
  const mtr = mtrs.find((row) => String(row?.id) === String(mtrRowId));
  return mtr?.candidates ?? [];
}

/** 寫入只送 mtr／stock／qty；不送 availableQty 等 UI 欄。 */
export function sanitizeSalesOrderAllocationForWrite(allocation) {
  const qty = Number(allocation?.qty);
  return {
    salesOrderLineMtrId: getSalesOrderAllocationMtrId(allocation),
    invStockId: getSalesOrderAllocationStockId(allocation),
    qty: Number.isFinite(qty) ? qty : allocation?.qty,
  };
}
